/**
 * Generic decision configuration for milestones with branching flows
 * (RFI, RFE, NOID, Audit) after the "submitted"/"filed" status.
 */

export interface DecisionOption {
  status: string;
  label: string;
}

export interface DecisionConfig {
  /** Statuses that belong to the decision panel (hidden from base flow buttons) */
  decisionStatuses: Set<string>;
  /** Intermediate options shown inside the decision panel (e.g. "Tem RFI") */
  intermediateOptions: DecisionOption[];
  /** Final outcome options (e.g. "Aprovado", "Negado") */
  finalOptions: DecisionOption[];
  /** Data fields to clear when resetting to a pre-decision status */
  fieldsToClean: string[];
}

const PWD_CONFIG: DecisionConfig = {
  decisionStatuses: new Set(['rfi', 'approved', 'denied']),
  intermediateOptions: [
    { status: 'rfi', label: 'Tem RFI' },
  ],
  finalOptions: [
    { status: 'approved', label: 'Aprovado' },
    { status: 'denied', label: 'Negado' },
  ],
  fieldsToClean: ['rfi_received_date', 'rfi_due_date', 'rfi_response_date'],
};

const PERM_CONFIG: DecisionConfig = {
  decisionStatuses: new Set(['rfi', 'audit', 'approved', 'denied']),
  intermediateOptions: [
    { status: 'rfi', label: 'Tem RFI' },
    { status: 'audit', label: 'Tem Auditoria' },
  ],
  finalOptions: [
    { status: 'approved', label: 'Aprovado' },
    { status: 'denied', label: 'Negado' },
  ],
  fieldsToClean: ['rfi_received_date', 'rfi_due_date', 'rfi_response_date', 'audit_date'],
};

/**
 * Padrão para TODAS as petições de imigração (USCIS): mesma experiência do
 * I-140 — Aprovado, Negado, "Tem RFE" e "Tem NOID", cada um com prazo próprio.
 * Não se aplica às etapas do Ministério do Trabalho (PWD, PERM, Recrutamento).
 */
export const USCIS_PETITION_CONFIG: DecisionConfig = {
  decisionStatuses: new Set([
    'rfe_received',
    'rfe_responded',
    'noid_received',
    'noid_responded',
    'approved',
    'denied',
  ]),
  intermediateOptions: [
    { status: 'rfe_received', label: 'Tem RFE' },
    { status: 'noid_received', label: 'Tem NOID' },
  ],
  finalOptions: [
    { status: 'approved', label: 'Aprovado' },
    { status: 'denied', label: 'Negado' },
  ],
  fieldsToClean: [
    'rfe_date',
    'rfe_deadline_date',
    'rfe_response_date',
    'noid_date',
    'noid_deadline_date',
    'noid_response_date',
  ],
};

const DOL_PREFIXES = ['pwd', 'perm', 'recrutamento', 'recruitment'];

function isDolMilestoneName(name: string) {
  const n = name.toLowerCase().trim();
  return DOL_PREFIXES.some((prefix) => n.startsWith(prefix));
}

/** Etapa própria de RFE/NOID (opcional), separada do formulário. */
export function isStandaloneRfeMilestone(name: string): boolean {
  return /^(rfe|noid)\b/i.test((name || '').trim());
}

/** Nomes de etapa que representam uma petição/aprovação junto ao USCIS. */
export function isUscisPetitionMilestone(name: string): boolean {
  if (!name) return false;
  if (isDolMilestoneName(name)) return false;
  if (isStandaloneRfeMilestone(name)) return false;
  const n = name.toLowerCase().trim();
  // Decisão do consulado não tem RFE/NOID do USCIS: Aprovado/Negado simples.
  if (/consular/.test(n)) return false;
  if (/^i-?\d{2,4}/.test(n)) return true;
  if (/\bi-?\d{2,4}\b/.test(n) && /(filing|petition|envio|aos|combo|filed)/.test(n)) return true;
  if (/envio\s*\(i-?\d/.test(n)) return true;
  // Ajuste de status (T/U, 3 anos) também é petição USCIS com decisão.
  if (/ajuste de status/.test(n)) return true;
  return /(filing|petition|aprova(ç|c)(ã|a)o)/.test(n);
}

const DECISION_CONFIGS: Array<{ test: (name: string) => boolean; config: DecisionConfig }> = [
  { test: (name) => name.toLowerCase().startsWith('pwd'), config: PWD_CONFIG },
  { test: (name) => name.toLowerCase().startsWith('perm'), config: PERM_CONFIG },
  { test: isUscisPetitionMilestone, config: USCIS_PETITION_CONFIG },
];

/**
 * Returns the decision config for a milestone name, or null if the milestone
 * doesn't have a decision panel.
 */
export function getDecisionConfig(name: string): DecisionConfig | null {
  if (!name) return null;
  for (const entry of DECISION_CONFIGS) {
    if (entry.test(name)) return entry.config;
  }
  return null;
}
